import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  ActionList,
  ActionListChildren,
  Box,
  Callout,
  EmptyState,
  EmptyStateProps,
  Illustration,
  Input,
  RowsSkeletonLoader,
} from '@node-space/ui-components'
import NoSearchResults from 'components/NoSearchResults/NoSearchResults'
import { MIN_PAGINATION_SIZE } from 'constants/queries'

export interface CustomerWalletsProps {
  isCustomerVerified: boolean
  customerName: string
  searchQuery: string
  isSearching: boolean
  isLoading: boolean
  walletTableRows: ActionListChildren[]
  AddWalletButton: EmptyStateProps['PrimaryButton']
  onChangeSearchQuery: (searchQuery: string) => void
}

export const CustomerWallets = ({
  isCustomerVerified,
  customerName,
  searchQuery,
  isSearching,
  isLoading,
  walletTableRows,
  AddWalletButton,
  onChangeSearchQuery,
}: CustomerWalletsProps) => {
  const { t } = useTranslation()
  const hasWallets = !!walletTableRows?.length

  const CustomerWalletView = useMemo(() => {
    if (isLoading) {
      return <RowsSkeletonLoader rows={MIN_PAGINATION_SIZE} />
    }

    if (!hasWallets) {
      return <NoSearchResults clearSearchCriteria={() => onChangeSearchQuery('')} />
    }

    return <ActionList id="customer-wallets-action-list" items={walletTableRows} />
  }, [isLoading, hasWallets, walletTableRows, onChangeSearchQuery])

  if (hasWallets || isSearching || isLoading) {
    return (
      <>
        <Box paddingB={24} width="full">
          <Input
            value={searchQuery}
            testId="customer-wallet-search-field"
            placeholder={t('customers.customerWalletSearchPlaceholder')}
            searchable
            onChange={event => onChangeSearchQuery(event.target.value)}
          />
        </Box>
        {!isCustomerVerified && (
          <Box paddingB={16}>
            <Callout variant="info">{t('customers.inactiveCustomerInfoMessage')}</Callout>
          </Box>
        )}
        {CustomerWalletView}
      </>
    )
  }

  if (!isCustomerVerified) {
    return (
      <EmptyState
        testId="customer-wallets-empty-state"
        title={t('customers.walletRestriction.customerNotVerifiedTitle')}
        description={t('customers.walletRestriction.customerNotVerifiedDescription')}
        Illustration={<Illustration name="NotVerified" />}
      />
    )
  }

  return (
    <EmptyState
      testId="customer-wallets-empty-state"
      title={t('customers.noCustomerWalletHeadline', { customerName })}
      description={t('customers.customerWalletDescription', { customerName })}
      Illustration={<Illustration name="NoData" />}
      PrimaryButton={AddWalletButton}
    />
  )
}
